<template>
  <EchoEditor
    v-model="content"
    class="w-full !rounded-lg [&>div]:rounded-lg shadow-none"
    :extensions="extensions"
    :hide-toolbar="hideToolbar"
    :hide-menubar="hideMenubar"
    :hide-bubble-menu="true"
    :hide-content-menu="true"
    :hide-table="true"
    :disabled="disabled"
    :max-height="512"
    output="html"
    :dark="theme === 'dark'"
    @blur="emit('blur')"
  >
  </EchoEditor>
</template>
<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { onMounted, ref, watch } from 'vue'
import { useAuth, useFiles } from '@/composables'
import {
  BaseKit,
  Bold,
  BulletList,
  Clear,
  Color,
  Columns,
  EchoEditor,
  Heading,
  Highlight,
  History,
  Image,
  ImageUpload,
  Italic,
  Link,
  OrderedList,
  Strike,
  TextAlign,
  Underline,
} from '@/notebook'

const props = defineProps(['modelValue', 'ticketId'])
const emit = defineEmits(['update:modelValue', 'blur'])
const content = useVModel(props, 'modelValue')
const theme = ref<string | null>(null)
const hideToolbar = ref<boolean>(false)
const hideMenubar = ref<boolean>(true)
const disabled = ref<boolean>(false)

// Use the files composable
const { handleFilesChange, files } = useFiles()
const { getToken } = useAuth()
const token = ref()

onMounted(async () => {
  token.value = await getToken()
  updateImagesWithToken(content.value)
})

const updateImagesWithToken = (htmlContent: string) => {
  if (!htmlContent || !token.value) return

  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlContent, 'text/html')
  let hasChanges = false

  // 1. Process blob URLs first
  const blobImages = doc.querySelectorAll('img[src^="blob:"]')

  if (blobImages.length > 0) {
    blobImages.forEach((img) => {
      const blobUrl = img.getAttribute('src')

      if (blobUrl) {
        const urlParts = blobUrl.split('/')
        const fileId = urlParts[urlParts.length - 1]

        if (fileId) {
          const permanentUrl = `${import.meta.env.VITE_API_BASE_URL}/download/${fileId}?access_token=${token.value}`

          img.setAttribute('src', permanentUrl)
          hasChanges = true
        }
      }
    })
  }

  const downloadImages = doc.querySelectorAll('img[src*="/download/"]')

  if (downloadImages.length > 0) {
    downloadImages.forEach((img) => {
      const currentSrc = img.getAttribute('src')

      if (currentSrc) {
        if (currentSrc.includes('/download/')) {
          const urlParts = currentSrc.split('/download/')

          if (urlParts.length > 1) {
            let fileId = urlParts[1]

            if (fileId.includes('?')) {
              fileId = fileId.split('?')[0]
            }

            const newSrc = `${import.meta.env.VITE_API_BASE_URL}/download/${fileId}?access_token=${token.value}`

            if (newSrc !== currentSrc) {
              img.setAttribute('src', newSrc)
              hasChanges = true
            }
          }
        }
      }
    })
  }

  if (hasChanges) {
    emit('update:modelValue', doc.body.innerHTML)
  }
}

const handleImageUpload = async (file: File): Promise<string> => {
  try {
    const result = await handleFilesChange([file], 'INTERNAL', props.ticketId)

    if (result && result.length > 0) {
      const url = `${import.meta.env.VITE_API_BASE_URL}/download/${result[0].fileId}?access_token=${token.value}`

      return url
    }

    return URL.createObjectURL(file)
  } catch (error) {
    console.error('Image upload error:', error)

    return URL.createObjectURL(file)
  }
}

watch(token, (newToken) => {
  if (newToken && content.value) {
    updateImagesWithToken(content.value)
  }
})

watch(
  files,
  (newFiles, oldFiles) => {
    if (oldFiles && oldFiles.length > newFiles.length) {
      const deletedFiles = oldFiles.filter((oldFile) => !newFiles.some((newFile) => newFile.id === oldFile.id))

      if (deletedFiles.length > 0 && content.value) {
        removeDeletedFilesFromContent(deletedFiles)
      }
    }
  },
  { deep: true },
)

const removeDeletedFilesFromContent = (deletedFiles) => {
  if (!content.value || typeof content.value !== 'string') return

  // Create a DOM parser to work with the HTML content
  const parser = new DOMParser()
  const doc = parser.parseFromString(content.value, 'text/html')
  let hasChanges = false

  // For each deleted file, find and remove image references
  deletedFiles.forEach((file: any) => {
    // Look for any img tags referencing this file ID
    const fileId = file.id
    const imagePattern = `/download/${fileId}`

    // Find all images in the content
    const images = doc.querySelectorAll('img')

    // Check each image to see if it references the deleted file
    images.forEach((img) => {
      const src = img.getAttribute('src')

      if (src && src.includes(imagePattern)) {
        // Remove the entire <img> element
        img.parentNode?.removeChild(img)
        hasChanges = true
      }
    })
  })
  if (hasChanges) {
    // Get the updated HTML and emit it
    emit('update:modelValue', doc.body.innerHTML)
  }
}

watch(content, (newContent) => {
  updateImagesWithToken(newContent)
})

const extensions = [
  BaseKit.configure({
    placeholder: {
      showOnlyCurrent: true,
    },
    characterCount: {
      limit: 50000,
    },
  }),
  History,
  Clear,
  Heading.configure({ spacer: true }),
  Bold,
  Italic,
  Underline,
  Strike,
  Color.configure({ spacer: true }),
  Highlight,
  BulletList,
  OrderedList,
  TextAlign.configure({ types: ['heading', 'paragraph', 'image'], spacer: true }),
  Link,
  Image,
  ImageUpload.configure({
    upload: (file: File) => {
      return handleImageUpload(file)
    },
  }),
  Columns,
]
</script>
